import { Button } from "@components/button";
import { FormLabel } from "@components/form";
import { Input } from "@components/input";
import { FormContainer, OnboardingContainer } from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { OnboardingPageType } from "@pages/onboarding/types";
import { X } from "@phosphor-icons/react";
import { useDebounceCallback } from "@react-hook/debounce";
import { cn } from "@utils/cn";
import { useLogError } from "@utils/error";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { z } from "zod";

export const linkFormSchema = z.object({
  links: z
    .array(z.string().url("Please enter a valid URL"))
    .min(1, "At least one link is required"),
});

export type LinkFormType = z.infer<typeof linkFormSchema>;
export const LinksForm: React.FC<
  OnboardingPageType & {
    title: string;
    subtitle: React.ReactNode;
    description: React.ReactNode;
  }
> = ({
  onBack,
  onSubmit,
  autoSave,
  onSkip,
  onSkipForNow,
  title,
  subtitle,
  description,
  footnote,
  exhibit,
  showSkipForNow,
  hideExitButton,
}) => {
  const { required, data } = exhibit;
  const { links }: { links?: string[] } = (data as LinkFormType) ?? {
    links: [""],
  };

  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const { isReadOnly } = useReadOnlyMode();
  const logError = useLogError();

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const methods = useForm({
    defaultValues: {
      links: links?.length ? links : [""],
    },
    resolver: zodResolver(linkFormSchema),
    mode: "onChange",
  });

  const { control, formState } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "links",
  });

  const watchedValues = useWatch({
    control,
  });

  const handleSave = useCallback(
    async (data: LinkFormType) => {
      const savePromise = (async () => {
        try {
          if (formState.isValid && data.links.some((link) => link)) {
            setStatus("loading");
            await autoSave(data);
            setStatus("success");
          }
        } catch (e) {
          setStatus("error");
          logError(e, { message: "Error saving links" });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [autoSave, formState.isValid, logError]
  );

  const handleSubmit = useCallback(
    async (data: LinkFormType) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await onSubmit(data);
        setStatus("success");
      } catch (e) {
        setStatus("error");
        logError(e, { message: "Error submitting links" });
      }
    },
    [onSubmit, logError]
  );

  const handleSkipForNow = useCallback(async () => {
    try {
      if (watchedValues?.links?.some((link) => link)) {
        await onSkipForNow?.(watchedValues);
      } else {
        onSkipForNow?.();
      }
    } catch (e) {
      logError(e, { message: "Error skipping" });
    }
  }, [watchedValues, onSkipForNow, logError]);

  const debouncedSave = useDebounceCallback(handleSave, 300);

  useEffect(() => {
    if (watchedValues?.links?.some((link) => link)) {
      debouncedSave(watchedValues as LinkFormType);
    }
  }, [watchedValues, debouncedSave]);

  useEffect(() => {
    if (fields.length < 1) {
      append("");
    }
  }, [fields, append]);

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={title}
        subtitle={subtitle}
        description={description}
        progress={0}
        onSubmit={methods.handleSubmit(handleSubmit)}
        exhibit={exhibit}
        name={`links-form`}
        onBack={onBack}
        onSkipForNow={required ? handleSkipForNow : onSkip}
        isLoading={status === "loading"}
        footnote={footnote}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem saving your links. Please try again."
          successMessage={
            status === "success" ? "Links saved successfully." : undefined
          }
        >
          <div>
            <div className="flex flex-col gap-3 mt-4">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="w-full h-fit p-4 bg-[#F7FCFF] rounded-[16px] border-[3px] border-dashed border-[#B7D3E8]"
                >
                  <div>
                    <FormLabel className="text-primary">
                      Link {index + 1}
                    </FormLabel>
                    <Controller
                      name={`links.${index}`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          disabled={isReadOnly}
                          className="w-full"
                          type="text"
                          placeholder="https://www.example.com"
                        />
                      )}
                    />
                    {formState.errors.links?.[index] && (
                      <p className="text-sm text-red-500 mt-1">
                        {formState.errors.links[index]?.message as string}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => remove(index)}
                    className={cn(
                      "absolute top-0 right-0 p-2",
                      isReadOnly && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <X />
                  </button>
                </div>
              ))}
            </div>

            {formState.isValid && (
              <Button
                type="button"
                variant="link"
                className="self-start text-accent px-0 mt-2"
                onClick={() => append("")}
                disabled={isReadOnly}
              >
                Add another link
              </Button>
            )}

            {formState.errors.links && formState.errors.links.message && (
              <p className="text-sm text-red-500 mt-2">
                {formState.errors.links.message as string}
              </p>
            )}
          </div>
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const MajorPublicationWorkLinks: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;

  return (
    <LinksForm
      {...props}
      subtitle={name}
      description={"Major Publication Work Links"}
      title={moduleName}
    />
  );
};
export const JournalArticleForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;

  return (
    <LinksForm
      {...props}
      subtitle={name}
      description={"Journal Articles"}
      title={moduleName}
    />
  );
};

export const LinkToEventForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;

  return (
    <LinksForm
      {...props}
      subtitle={name}
      description={"A link to the event where you participated as a judge."}
      title={moduleName}
    />
  );
};

export default LinksForm;
